import {
  MorphemeInfoView,
  type MorphemeInfoViewProps,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.info";
import { GraphemeInfoCaligraphyScreen } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.info._screens.caligraphy";
import { GraphemeInfoDictionaryScreen } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.info._screens.dictionary";
import { GraphemeInfoLearningProgressScreen } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.info._screens.learning-progress";
import { resolveRouteParam, useRouter } from "@-/com.npmjs.expo-router";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { MorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { MorphemeInlineNavigationContext } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { useContext, type ReactElement } from "react";

const graphemeReferenceTabList = [
  { segment: "dictionary", title: "Dictionary" },
  { segment: "caligraphy", title: "Caligraphy" },
  { segment: "progress", title: "Progress" },
] as const;

const screenRenderers: Record<string, (grapheme: string) => ReactElement> = {
  caligraphy: (grapheme) => <GraphemeInfoCaligraphyScreen grapheme={grapheme} />,
  dictionary: (grapheme) => <GraphemeInfoDictionaryScreen grapheme={grapheme} />,
  progress: (grapheme) => <GraphemeInfoLearningProgressScreen grapheme={grapheme} />,
};

export function GraphemeInfoView({
  grapheme,
  tabBarAccessory,
}: {
  grapheme: string;
  tabBarAccessory?: MorphemeInfoViewProps["tabBarAccessory"];
}) {
  const router = useRouter();
  const inlineNavigate = useContext(MorphemeInlineNavigationContext);
  // The initial info sub-tab is read from THIS panel route's own `tab` param, threaded through
  // `MorphemePanelsContext` (populated above the MaterialTopTabs navigator) rather than
  // `useGlobalSearchParams` / a tab-local `useLocalSearchParams` — a lazily-mounted / mid-swipe tab
  // screen resolves local params to the focused route, and a global read reflected whichever panel
  // route was globally focused. The info view follows changes to this value because it may
  // already be pre-mounted when a link switches from Mnemonics to a specific info tab; the
  // `router.setParams` write below keeps user-driven tab selections in the URL too.
  const [morphemeHrefObject] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.morphemeHrefObject,
  );
  const tabParam = resolveRouteParam(morphemeHrefObject.params.tab) || undefined;

  return (
    <MorphemeInfoView
      initialTabSegment={tabParam === "mnemonics" ? "dictionary" : (tabParam ?? "dictionary")}
      morpheme={grapheme}
      onActiveTabChange={(segment) => {
        if (inlineNavigate !== null) {
          return;
        }
        // Mirror the selection into the URL so it survives a refresh / deep link.
        // `setParams` throws if the navigation store isn't ready (e.g. the view
        // rendered standalone in tests); the local activeIndex above already
        // drives the UI, so the URL sync is best-effort.
        try {
          router.setParams({ tab: segment });
        } catch {
          // No active navigation container — skip the URL sync.
        }
      }}
      screenRenderers={screenRenderers}
      stateKeyPrefix="morpheme-info"
      tabBarAccessory={tabBarAccessory}
      tabList={graphemeReferenceTabList}
    />
  );
}
